import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CODICI_CONTATORE, DESCRIZIONE_CONTATORE } from '@praticheenergia/domain'
import {
  SchemaContatore,
  aggiornaContatore,
  creaContatore,
  elencaContatori,
  eliminaContatore,
  type Contatore,
  type DatiContatore,
} from './contatori.api'

interface Props {
  readonly impiantoId: number
}

/** Lettere ammesse dal tracciato (vincolo `^[A-KRS]$`, 0030_motore_mensile.sql). */
const TIPI_SCAMBIO = [...'ABCDEFGHIJK', 'R', 'S']

const VUOTO: DatiContatore = {
  codice: 'produzione',
  matricola: '',
  cifreIntere: 0,
  costanteLettura: '1',
  kwhDiretti: false,
  tipoScambio: '',
  idControparte: '',
  tipologiaConsumo: '',
}

const daContatore = (c: Contatore): DatiContatore => ({
  codice: c.codice,
  matricola: c.matricola,
  cifreIntere: c.cifreIntere,
  costanteLettura: c.costanteLettura,
  kwhDiretti: c.kwhDiretti,
  tipoScambio: c.tipoScambio ?? '',
  idControparte: c.idControparte ?? '',
  tipologiaConsumo: c.tipologiaConsumo ?? '',
})

type Campo = keyof DatiContatore

export function GestioneContatori({ impiantoId }: Props) {
  const queryClient = useQueryClient()
  // `null` = dialogo chiuso; id `null` = contatore nuovo.
  const [modulo, setModulo] = useState<{ id: number | null; dati: DatiContatore } | null>(null)
  const [erroriCampo, setErroriCampo] = useState<Partial<Record<Campo, string>>>({})
  const [errore, setErrore] = useState<string | null>(null)

  const { data: contatori = [], isLoading, error } = useQuery({
    queryKey: ['contatori', impiantoId],
    queryFn: () => elencaContatori(impiantoId),
  })

  // I contatori entrano in POD, letture e stato delle dichiarazioni (trigger
  // 0032): si rinfresca tutto invece di elencare chiavi che invecchiano.
  const rinfresca = () => queryClient.invalidateQueries()

  function apri(id: number | null, dati: DatiContatore) {
    setErroriCampo({})
    setErrore(null)
    setModulo({ id, dati })
  }

  function imposta<K extends Campo>(campo: K, valore: DatiContatore[K]) {
    setModulo((m) => (m === null ? m : { ...m, dati: { ...m.dati, [campo]: valore } }))
  }

  async function suSalva() {
    if (modulo === null) return
    const esito = SchemaContatore.safeParse(modulo.dati)
    if (!esito.success) {
      const errori: Partial<Record<Campo, string>> = {}
      for (const i of esito.error.issues) {
        const campo = i.path[0] as Campo
        errori[campo] ??= i.message
      }
      setErroriCampo(errori)
      return
    }
    setErroriCampo({})
    setErrore(null)
    try {
      if (modulo.id === null) await creaContatore(impiantoId, modulo.dati)
      else await aggiornaContatore(modulo.id, modulo.dati)
      setModulo(null)
      await rinfresca()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  async function suElimina(c: Contatore) {
    if (!window.confirm(`Eliminare il contatore ${c.matricola} e tutte le sue letture?`)) return
    setErrore(null)
    try {
      await eliminaContatore(c.id)
      await rinfresca()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  const campoTesto = (campo: Campo, etichetta: string) => (
    <TextField
      size="small"
      label={etichetta}
      value={modulo?.dati[campo] ?? ''}
      onChange={(e) => imposta(campo, e.target.value as never)}
      error={erroriCampo[campo] !== undefined}
      helperText={erroriCampo[campo]}
    />
  )

  return (
    <Box>
      {error && <Alert severity="error" role="alert">{error instanceof Error ? error.message : 'Errore nel caricamento'}</Alert>}
      {errore !== null && modulo === null && <Alert severity="error" role="alert">{errore}</Alert>}
      {!isLoading && contatori.length === 0 && <p>Nessun contatore per questo impianto</p>}
      {contatori.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Matricola</TableCell>
              <TableCell>Codice</TableCell>
              <TableCell>Cifre intere</TableCell>
              <TableCell>Scambio</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {contatori.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.matricola}</TableCell>
                <TableCell>{DESCRIZIONE_CONTATORE[c.codice]}</TableCell>
                <TableCell>{c.cifreIntere}</TableCell>
                <TableCell>{[c.tipoScambio, c.idControparte].filter(Boolean).join(' · ')}</TableCell>
                <TableCell align="right">
                  <Button size="small" aria-label={`Modifica ${c.matricola}`} onClick={() => apri(c.id, daContatore(c))}>
                    Modifica
                  </Button>
                  <Button size="small" color="error" aria-label={`Elimina ${c.matricola}`} onClick={() => void suElimina(c)}>
                    Elimina
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Button variant="outlined" sx={{ mt: 2 }} onClick={() => apri(null, VUOTO)}>
        Aggiungi contatore
      </Button>

      <Dialog open={modulo !== null} onClose={() => setModulo(null)} fullWidth maxWidth="sm">
        <DialogTitle>{modulo?.id === null ? 'Nuovo contatore' : 'Modifica contatore'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            <TextField
              select
              size="small"
              label="Codice"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              value={modulo?.dati.codice ?? 'produzione'}
              onChange={(e) => imposta('codice', e.target.value as DatiContatore['codice'])}
            >
              {CODICI_CONTATORE.map((c) => (
                <option key={c} value={c}>
                  {DESCRIZIONE_CONTATORE[c]}
                </option>
              ))}
            </TextField>
            {campoTesto('matricola', 'Matricola')}
            <TextField
              size="small"
              type="number"
              label="Cifre intere"
              value={modulo?.dati.cifreIntere ?? 0}
              onChange={(e) => imposta('cifreIntere', e.target.value === '' ? 0 : Number(e.target.value))}
              error={erroriCampo.cifreIntere !== undefined}
              helperText={erroriCampo.cifreIntere}
            />
            {campoTesto('costanteLettura', 'Costante di lettura')}
            <FormControlLabel
              control={
                <Checkbox
                  checked={modulo?.dati.kwhDiretti ?? false}
                  onChange={(e) => imposta('kwhDiretti', e.target.checked)}
                />
              }
              label="Letture in kWh diretti"
            />
            <TextField
              select
              size="small"
              label="Tipo di scambio"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              value={modulo?.dati.tipoScambio ?? ''}
              onChange={(e) => imposta('tipoScambio', e.target.value)}
              error={erroriCampo.tipoScambio !== undefined}
              helperText={erroriCampo.tipoScambio}
            >
              <option value="">—</option>
              {TIPI_SCAMBIO.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </TextField>
            {campoTesto('idControparte', 'Id controparte')}
            {campoTesto('tipologiaConsumo', 'Tipologia di consumo')}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModulo(null)}>Annulla</Button>
          <Button variant="contained" onClick={() => void suSalva()}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
